import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  FormControl,
  Switch,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { CustomizationRichTextEditor, wysiwygEditorBoxSx } from './CustomizationRichTextEditor';

const DEFAULT_GREETING = 'Hi there, how can I help you today?';
const DEFAULT_FALLBACK = "I don't have enough information to answer that question.";

function isHtmlEffectivelyEmpty(html) {
  if (html == null || String(html).trim() === '') return true;
  const stripped = String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped === '';
}

const GreetingFallbackModal = ({
  open,
  onClose,
  customization,
  setCustomization,
  setHasUnsavedChanges,
  onSave,
}) => {
  const [saving, setSaving] = useState(false);

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    setCustomization((prev) => {
      const updated = { ...prev, [key]: value };
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return updated;
    });
  };

  const handleGreetingSwitch = (e) => {
    const checked = e.target.checked;
    setCustomization((prev) => {
      let greetingText = prev.greetingText;
      if (checked && isHtmlEffectivelyEmpty(greetingText)) {
        greetingText = DEFAULT_GREETING;
      }
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return { ...prev, greetingEnabled: checked, greetingText };
    });
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      const success = await onSave(customization);
      if (success) {
        onClose();
      }
    } catch (err) {
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 720,
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pr: 5, flexShrink: 0 }}>
        Greeting & Fallback
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarGutter: 'stable',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box sx={{ maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', pt: 1, pb: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Set a custom greeting message that will be shown to users when the chat opens. Toggle off to disable the
            greeting.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography>Show Agent Greeting</Typography>
              <Switch checked={customization.greetingEnabled !== false} onChange={handleGreetingSwitch} color="primary" />
            </Box>
          </FormControl>
          {customization.greetingEnabled !== false && (
            <Box sx={wysiwygEditorBoxSx}>
              <CustomizationRichTextEditor
                value={customization.greetingText !== undefined ? customization.greetingText : DEFAULT_GREETING}
                onChange={handleChange('greetingText')}
                placeholder={DEFAULT_GREETING}
                enableImageTools
              />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            This message will be shown when the chat is first opened or reopened from a closed state.
          </Typography>

          <Divider sx={{ my: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography>Show Custom Fallback Message</Typography>
              <Switch
                checked={customization.fallbackEnabled !== false}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHasUnsavedChanges && setHasUnsavedChanges(true);
                  setCustomization((prev) => {
                    let fallbackText = prev.fallbackText;
                    if (checked && isHtmlEffectivelyEmpty(fallbackText)) {
                      fallbackText = DEFAULT_FALLBACK;
                    }
                    return { ...prev, fallbackEnabled: checked, fallbackText };
                  });
                }}
                color="primary"
              />
            </Box>
          </FormControl>
          {customization.fallbackEnabled !== false && (
            <Box sx={wysiwygEditorBoxSx}>
              <CustomizationRichTextEditor
                value={customization.fallbackText !== undefined ? customization.fallbackText : DEFAULT_FALLBACK}
                onChange={handleChange('fallbackText')}
                placeholder={DEFAULT_FALLBACK}
                enableImageTools
              />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            This message will be shown when the agent cannot answer a question due to insufficient information.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GreetingFallbackModal;
